import { useContext, useMemo, useRef } from "react";
import { onSnapshot } from "firebase/firestore";
import { resetUserRoomStatus } from "modules/room/redux/roomSlice";
import { useShuffleAttendees } from "modules/shuffleAttendees/hooks/useShuffleAttendees";
import useNotificationActions from "modules/notification/hooks/useNotificationActions";
import { Events, TRACKING_CONTEXT } from "modules/tracking";
import { useI18n } from "i18n";
import { getUnixTimestamp } from "helpers/time/format";
import { useAppDispatch } from "store/hooks";
import { useUserData } from "modules/audioVideo/hooks/useUserData";
import { getCurrentRoomId } from "helpers/reduxHelper";
import { useParticipantCounts } from "@daily-co/daily-react";
import { useWhereAmI } from "modules/mapNavigation/hooks/useWhereAmI/useWhereAmI";
import { ShuffleStates } from "../../constants";
import {
  hideInitShuffleBanner,
  hideShuffleAttendeesCountdownBanner,
  hideShuffleAttendeesCountdownDialog,
  hideShuffleAttendeesDialog,
  setShuffleStartTime,
  showInitShuffleBanner,
  showShuffleAttendeesCountdownDialog,
} from "../../redux/actions";

/**
 * We use a separate hook for subscribing and unsubscribing to firestore document
 * because we want this hook to be called only once.
 * We need this because we want a reference to the listener so that we can unsubscribe later
 * when the component is unmounted.
 * Doing this in the common component is not possible because we will lose reference to the
 * listener when the hook is called from another component.
 */
export const useShuffleAttendeesFirestore = () => {
  const dispatch = useAppDispatch();
  const { addInfoNotification, addErrorNotification } =
    useNotificationActions();

  const { getShuffleAttendeesDoc } = useShuffleAttendees();
  const { track } = useContext(TRACKING_CONTEXT);
  const { hidden, present } = useParticipantCounts();
  const { showWhereAmI } = useWhereAmI();

  const liveUsersCount = useMemo(() => hidden + present, [hidden, present]);
  const { t } = useI18n(["event"]);

  const { setConversationId } = useUserData();

  const shuffleListener = useRef<{ listener: null | (() => void) }>({
    listener: null,
  });

  const subscribeToShuffleEvents = (eventId: string) => {
    shuffleListener.current.listener = onSnapshot(
      getShuffleAttendeesDoc(eventId),
      (change) => {
        const data = change.data() || {};

        if (
          data?.status === ShuffleStates.CANCELLED ||
          data?.status === ShuffleStates.ERROR
        ) {
          dispatch(hideShuffleAttendeesDialog());
          dispatch(hideShuffleAttendeesCountdownDialog());
          dispatch(hideShuffleAttendeesCountdownBanner());
          dispatch(hideInitShuffleBanner());

          const timeThreshold = getUnixTimestamp() - 5;
          if (data?.timestamp?.seconds > timeThreshold) {
            // Show notification if shuffle was cancelled in the last 5 seconds
            // We do this because if the user refreshes the page, this code will be executed again
            // We don't want to show this notification then
            if (data?.status === ShuffleStates.ERROR) {
              addErrorNotification({
                message: t("event:shuffle.attendees.error"),
              });
              track(Events.SHUFFLE_ERROR);
            } else {
              addInfoNotification({
                message: t("event:shuffle.attendees.cancelled"),
              });
            }
          }

          return;
        }

        if (
          (data?.status === ShuffleStates.COUNTDOWN ||
            data?.status === ShuffleStates.SCHEDULED) &&
          data?.timestamp
        ) {
          // Save the start time in a redux store
          // We do this because we need to use this time in the MapBanner component
          // where we do not have the ability to pass this as a prop
          // We also cannot use a variable in the hook because it will be reset
          // every time we call this hook.
          dispatch(setShuffleStartTime(data?.timestamp?.seconds));

          dispatch(hideShuffleAttendeesDialog());
          dispatch(hideInitShuffleBanner());
          dispatch(hideShuffleAttendeesCountdownBanner());
          dispatch(showShuffleAttendeesCountdownDialog());

          // Track on amplitude
          track(Events.SHUFFLE_CLICKED);

          return;
        }

        if (
          data?.status === ShuffleStates.IN_PROGRESS ||
          data?.status === ShuffleStates.SUCCESS ||
          data?.status === ShuffleStates.EXECUTING
        ) {
          // Shuffle algo has started running
          dispatch(hideShuffleAttendeesDialog());
          dispatch(hideShuffleAttendeesCountdownBanner());
          dispatch(hideShuffleAttendeesCountdownDialog());

          if (data?.status === ShuffleStates.IN_PROGRESS) {
            dispatch(showInitShuffleBanner());
          } else if (data?.status === ShuffleStates.SUCCESS) {
            dispatch(hideInitShuffleBanner());
            const tableId = getCurrentRoomId();
            if (tableId) {
              setConversationId({ conversationId: tableId });

              const timeThreshold = getUnixTimestamp() - 3;
              // show where-am-I if shuffle timestamp updated in the last three seconds
              // this runs even when SUCCESS state has been persisted for more than three seconds and
              // we don't want to keep it running then
              if (data?.timestamp?.seconds > timeThreshold) {
                showWhereAmI();
              }
            }
          } else {
            // Reset user room status only if we are executing shuffle
            dispatch(resetUserRoomStatus());
          }

          // Amplitude tracking
          if (data?.status === ShuffleStates.IN_PROGRESS) {
            track(Events.SHUFFLE_STARTED);
          } else {
            track(Events.SHUFFLE_COMPLETED, {
              guests: Math.max(liveUsersCount, 1),
            });
          }
        }
      },
    );
  };

  const unsubscribeToShuffleEvents = () => {
    if (shuffleListener.current.listener) {
      shuffleListener.current.listener();
    }
  };

  return {
    subscribeToShuffleEvents,
    unsubscribeToShuffleEvents,
  };
};
